<template>
  <div class="page-workbench">
    <!-- 左侧：章节目录 -->
    <div class="left-panel">
      <div class="panel-header">
        <span class="panel-title">作品结构</span>
        <div class="panel-actions">
          <el-tooltip content="新增卷" placement="top">
            <el-button type="text" size="mini" icon="el-icon-plus" @click="openAddDialog(0)">卷</el-button>
          </el-tooltip>
        </div>
      </div>

      <div v-if="treeLoading" class="tree-loading">
        <i class="el-icon-loading" /> 加载中...
      </div>

      <el-tree
        v-else
        :data="outline"
        node-key="id"
        highlight-current
        default-expand-all
        :props="treeProps"
        :current-node-key="currentChapterId"
        class="chapter-tree"
        @node-click="handleNodeClick"
      >
        <span slot-scope="{ node, data }" class="tree-node">
          <span class="tree-label" :title="node.label">{{ node.label }}</span>
          <span class="tree-ops">
            <el-tooltip v-if="data.parentId === 0 || data.parentId === null" content="新增章节" placement="top">
              <i class="el-icon-plus tree-op-btn" @click.stop="openAddDialog(data.id)" />
            </el-tooltip>
            <el-tooltip content="删除" placement="top">
              <i class="el-icon-delete tree-op-btn tree-op-del" @click.stop="handleDelete(data)" />
            </el-tooltip>
          </span>
        </span>
      </el-tree>

      <div v-if="!treeLoading && outline.length === 0" class="tree-empty">
        <p>暂无章节</p>
        <el-button size="mini" type="primary" plain @click="openAddDialog(0)">添加第一卷</el-button>
      </div>
    </div>

    <!-- 中间：编辑器 -->
    <div class="center-panel">
      <div class="panel-header">
        <div class="chapter-title-wrap">
          <el-input
            v-model="chapterTitle"
            size="small"
            placeholder="章节标题"
            :disabled="!currentChapterId || currentChapterIsVolume"
            @blur="handleTitleBlur"
          />
        </div>
        <div class="chapter-meta">
          <span>{{ wordCount }} 字</span>
          <span :class="saveStatusClass">{{ saveStatusText }}</span>
        </div>
      </div>
      <div class="editor-wrapper">
        <div v-if="!currentChapterId" class="editor-placeholder">
          <i class="el-icon-edit-outline" />
          <p>从左侧选择章节开始创作</p>
        </div>
        <div v-else-if="currentChapterIsVolume" class="editor-placeholder">
          <i class="el-icon-folder-opened" />
          <p>这是一个卷，请在下方章节中创作</p>
        </div>
        <el-input
          v-else
          v-model="content"
          type="textarea"
          :rows="contentRows"
          class="editor"
          placeholder="在这里开始你的故事..."
          @input="handleContentInput"
        />
      </div>
    </div>

    <!-- 右侧：AI助手 & 人物设定 -->
    <div class="right-panel">
      <el-tabs v-model="rightTab" stretch>
        <el-tab-pane label="AI 助手" name="ai">
          <div class="ai-tools">
            <el-button type="primary" size="mini" @click="mockAi('continue')">续写</el-button>
            <el-button size="mini" @click="mockAi('polish')">润色</el-button>
            <el-button size="mini" @click="mockAi('rewrite')">改写</el-button>
          </div>
          <div v-if="aiText" class="ai-result">
            <div class="ai-result-title">AI 建议</div>
            <div class="ai-result-body">{{ aiText }}</div>
          </div>
        </el-tab-pane>
        <el-tab-pane label="人物设定" name="role">
          <div class="role-panel-header">
            <span class="role-panel-count">共 {{ roles.length }} 位角色</span>
            <el-button size="mini" type="primary" plain icon="el-icon-plus" @click="openRoleDialog(null)">
              新增角色
            </el-button>
          </div>
          <div v-if="rolesLoading" class="role-loading">
            <i class="el-icon-loading" /> 加载中...
          </div>
          <ul v-else class="setting-list">
            <li v-for="role in roles" :key="role.id" class="setting-item">
              <div class="setting-title">
                <span>{{ role.name || '未命名' }}</span>
                <span class="setting-badge" :class="role.roleType === 'main' ? 'badge-main' : 'badge-support'">
                  {{ role.roleType === 'main' ? '主角' : '配角' }}
                </span>
                <span class="setting-ops">
                  <i class="el-icon-edit setting-op-btn" @click="openRoleDialog(role)" />
                  <i class="el-icon-delete setting-op-del" @click="handleDeleteRole(role)" />
                </span>
              </div>
              <div v-if="role.identity || role.gender || role.age" class="setting-meta">
                <span v-if="role.identity">{{ role.identity }}</span>
                <span v-if="role.gender">{{ role.gender }}</span>
                <span v-if="role.age">{{ role.age }}</span>
              </div>
              <div v-if="role.trait" class="setting-desc">{{ role.trait }}</div>
              <div v-if="role.bio" class="setting-bio">{{ role.bio }}</div>
            </li>
            <li v-if="!rolesLoading && roles.length === 0" class="setting-empty">
              <p>暂无角色，点击上方"新增角色"开始设定</p>
            </li>
          </ul>
        </el-tab-pane>
      </el-tabs>
    </div>

    <!-- 新增卷/章节弹窗 -->
    <el-dialog
      :title="addDialog.parentId === 0 ? '新增卷' : '新增章节'"
      :visible.sync="addDialog.visible"
      width="400px"
    >
      <el-form :model="addDialog" label-width="60px" size="small">
        <el-form-item :label="addDialog.parentId === 0 ? '卷名' : '章节名'">
          <el-input
            v-model="addDialog.title"
            :placeholder="addDialog.parentId === 0 ? '如：第一卷 · 初启' : '如：第一章 序幕'"
            @keyup.enter.native="submitAdd"
          />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="addDialog.visible = false">取消</el-button>
        <el-button type="primary" :loading="addDialog.saving" @click="submitAdd">确定</el-button>
      </span>
    </el-dialog>

    <!-- 新增/编辑角色弹窗 -->
    <el-dialog
      :title="roleDialog.isEdit ? '编辑角色' : '新增角色'"
      :visible.sync="roleDialog.visible"
      width="480px"
    >
      <el-form :model="roleDialog.form" label-width="70px" size="small">
        <el-form-item label="角色类型">
          <el-radio-group v-model="roleDialog.form.roleType">
            <el-radio label="main">主角</el-radio>
            <el-radio label="support">配角</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="姓名">
          <el-input v-model="roleDialog.form.name" maxlength="10" show-word-limit placeholder="请输入角色姓名" />
        </el-form-item>
        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="性别">
              <el-input v-model="roleDialog.form.gender" maxlength="3" placeholder="如：男 / 女" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="年龄">
              <el-input v-model="roleDialog.form.age" maxlength="8" placeholder="如：18岁" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="身份">
          <el-input v-model="roleDialog.form.identity" maxlength="50" placeholder="如：侯府嫡女、修炼者" />
        </el-form-item>
        <el-form-item label="性格">
          <el-input v-model="roleDialog.form.trait" maxlength="50" placeholder="如：坚韧果敢、心思缜密" />
        </el-form-item>
        <el-form-item label="角色小传">
          <el-input
            v-model="roleDialog.form.bio"
            type="textarea"
            :rows="4"
            maxlength="300"
            show-word-limit
            placeholder="简要介绍角色的背景故事..."
          />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="roleDialog.visible = false">取消</el-button>
        <el-button type="primary" :loading="roleDialog.saving" @click="submitRole">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getChapterTree, getChapter, createChapter, updateChapter, deleteChapter } from '@/api/chapter';
import { listRoles, createRole, updateRole, deleteRole } from '@/api/role';

export default {
  name: 'Workbench',
  data() {
    return {
      projectId: null,
      treeLoading: false,
      outline: [],
      treeProps: { label: 'title', children: 'children' },
      currentChapterId: null,
      currentChapterIsVolume: false,
      chapterTitle: '',
      content: '',
      rightTab: 'ai',
      aiText: '',
      roles: [],
      rolesLoading: false,
      // 自动保存
      saveTimer: null,
      saveStatus: 'idle', // idle | saving | saved | error
      // 新增卷/章节弹窗
      addDialog: {
        visible: false,
        parentId: 0,
        title: '',
        saving: false
      },
      // 角色编辑弹窗
      roleDialog: {
        visible: false,
        isEdit: false,
        saving: false,
        form: {
          id: null,
          roleType: 'main',
          name: '',
          gender: '',
          age: '',
          identity: '',
          trait: '',
          bio: ''
        }
      }
    };
  },
  computed: {
    wordCount() {
      return this.content ? this.content.replace(/\s/g, '').length : 0;
    },
    saveStatusText() {
      const map = { idle: '', saving: '保存中…', saved: '已保存', error: '保存失败' };
      return map[this.saveStatus] || '';
    },
    saveStatusClass() {
      return {
        'save-status': true,
        'save-saving': this.saveStatus === 'saving',
        'save-saved': this.saveStatus === 'saved',
        'save-error': this.saveStatus === 'error'
      };
    },
    contentRows() {
      const winH = window.innerHeight || 800;
      return Math.max(16, Math.floor((winH - 160) / 22));
    }
  },
  created() {
    this.projectId = Number(this.$route.params.id);
    this.loadTree();
    this.loadRoles();
  },
  beforeDestroy() {
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.doSave();
  },
  methods: {
    // ========== 章节目录 ==========
    async loadTree() {
      this.treeLoading = true;
      try {
        const res = await getChapterTree(this.projectId);
        this.outline = res.data || [];
        // 默认选中第一个叶子章节
        if (this.outline.length > 0 && !this.currentChapterId) {
          const first = this.outline[0];
          if (first.children && first.children.length > 0) {
            this.selectChapter(first.children[0]);
          } else {
            this.selectChapter(first);
          }
        }
      } finally {
        this.treeLoading = false;
      }
    },

    // ========== 人物设定 ==========
    async loadRoles() {
      this.rolesLoading = true;
      try {
        const res = await listRoles(this.projectId);
        this.roles = res.data || [];
      } catch (e) {
        this.roles = [];
      } finally {
        this.rolesLoading = false;
      }
    },

    openRoleDialog(role) {
      this.roleDialog.isEdit = !!role;
      if (role) {
        this.roleDialog.form = {
          id: role.id,
          roleType: role.roleType || 'main',
          name: role.name || '',
          gender: role.gender || '',
          age: role.age || '',
          identity: role.identity || '',
          trait: role.trait || '',
          bio: role.bio || ''
        };
      } else {
        this.roleDialog.form = {
          id: null,
          roleType: 'main',
          name: '',
          gender: '',
          age: '',
          identity: '',
          trait: '',
          bio: ''
        };
      }
      this.roleDialog.visible = true;
    },

    async submitRole() {
      if (!this.roleDialog.form.name.trim()) {
        this.$message.warning('请输入角色姓名');
        return;
      }
      this.roleDialog.saving = true;
      try {
        const form = this.roleDialog.form;
        if (this.roleDialog.isEdit) {
          await updateRole(form.id, {
            roleType: form.roleType,
            name: form.name,
            gender: form.gender,
            age: form.age,
            identity: form.identity,
            trait: form.trait,
            bio: form.bio
          });
          this.$message.success('保存成功');
        } else {
          await createRole({
            projectId: this.projectId,
            roleType: form.roleType,
            name: form.name,
            gender: form.gender,
            age: form.age,
            identity: form.identity,
            trait: form.trait,
            bio: form.bio
          });
          this.$message.success('角色添加成功');
        }
        this.roleDialog.visible = false;
        await this.loadRoles();
      } finally {
        this.roleDialog.saving = false;
      }
    },

    handleDeleteRole(role) {
      this.$confirm(`确定删除角色「${role.name || '未命名'}」吗？`, '删除确认', {
        type: 'warning',
        confirmButtonText: '删除',
        confirmButtonClass: 'el-button--danger'
      }).then(async () => {
        await deleteRole(role.id);
        this.$message.success('已删除');
        await this.loadRoles();
      }).catch(() => {});
    },

    handleNodeClick(data) {
      if (this.saveTimer) {
        clearTimeout(this.saveTimer);
        this.doSave();
      }
      this.selectChapter(data);
    },

    async selectChapter(data) {
      const isVolume = !data.parentId || data.parentId === 0;
      this.currentChapterId = data.id;
      this.currentChapterIsVolume = isVolume;
      this.chapterTitle = data.title;
      this.content = '';
      this.saveStatus = 'idle';

      if (!isVolume) {
        try {
          const res = await getChapter(data.id);
          const chapter = res.data || {};
          this.content = chapter.content || '';
          this.chapterTitle = chapter.title || data.title;
        } catch (e) {
          // ignore
        }
      }
    },

    // ========== 新增 ==========
    openAddDialog(parentId) {
      this.addDialog.parentId = parentId;
      this.addDialog.title = '';
      this.addDialog.visible = true;
      this.$nextTick(() => {
        const input = this.$el.querySelector('.el-dialog .el-input__inner');
        if (input) input.focus();
      });
    },

    async submitAdd() {
      if (!this.addDialog.title.trim()) {
        this.$message.warning('请输入标题');
        return;
      }
      this.addDialog.saving = true;
      try {
        await createChapter({
          projectId: this.projectId,
          parentId: this.addDialog.parentId,
          title: this.addDialog.title.trim()
        });
        this.$message.success('添加成功');
        this.addDialog.visible = false;
        await this.loadTree();
      } finally {
        this.addDialog.saving = false;
      }
    },

    // ========== 删除 ==========
    handleDelete(data) {
      const isVolume = !data.parentId || data.parentId === 0;
      const msg = isVolume
        ? `确定删除卷「${data.title}」及其下所有章节吗？`
        : `确定删除章节「${data.title}」吗？`;
      this.$confirm(msg, '删除确认', {
        type: 'warning',
        confirmButtonText: '删除',
        confirmButtonClass: 'el-button--danger'
      }).then(async () => {
        await deleteChapter(data.id);
        this.$message.success('已删除');
        if (this.currentChapterId === data.id) {
          this.currentChapterId = null;
          this.content = '';
          this.chapterTitle = '';
        }
        await this.loadTree();
      }).catch(() => {});
    },

    // ========== 标题修改 ==========
    async handleTitleBlur() {
      if (!this.currentChapterId || !this.chapterTitle.trim()) return;
      try {
        await updateChapter(this.currentChapterId, { title: this.chapterTitle.trim() });
        // 同步更新树节点标题
        this.updateTreeNodeTitle(this.outline, this.currentChapterId, this.chapterTitle.trim());
      } catch (e) {
        // ignore
      }
    },

    updateTreeNodeTitle(nodes, id, title) {
      for (const node of nodes) {
        if (node.id === id) {
          node.title = title;
          return true;
        }
        if (node.children && node.children.length) {
          if (this.updateTreeNodeTitle(node.children, id, title)) return true;
        }
      }
      return false;
    },

    // ========== 正文自动保存 ==========
    handleContentInput() {
      this.saveStatus = 'idle';
      if (this.saveTimer) clearTimeout(this.saveTimer);
      this.saveTimer = setTimeout(() => this.doSave(), 2000);
    },

    async doSave() {
      if (!this.currentChapterId || this.currentChapterIsVolume) return;
      if (this.saveStatus === 'saving') return;
      this.saveStatus = 'saving';
      try {
        await updateChapter(this.currentChapterId, {
          content: this.content,
          title: this.chapterTitle
        });
        this.saveStatus = 'saved';
      } catch (e) {
        this.saveStatus = 'error';
      }
    },

    // ========== AI 模拟 ==========
    mockAi(type) {
      const map = {
        continue: '【续写示例】夜色压得更低，街灯被雾气吞噬，只剩下远处隐约的脚步声……',
        polish: '【润色示例】将平铺直叙的句子改写得更具画面感和节奏感。',
        rewrite: '【改写示例】从另一位角色的视角重新讲述当前片段。'
      };
      this.aiText = map[type] || '';
    }
  }
};
</script>

<style scoped>
.page-workbench {
  display: grid;
  grid-template-columns: 240px 1fr 300px;
  height: 100%;
  background: #f5f7fa;
  color: #303133;
  overflow: hidden;
}
.left-panel,
.center-panel,
.right-panel {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-right: 1px solid #ebeef5;
}
.right-panel {
  border-right: none;
  background: #ffffff;
}
.left-panel {
  background: #ffffff;
}
.panel-header {
  height: 48px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  border-bottom: 1px solid #ebeef5;
  background: #ffffff;
}
.panel-title {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
}
.panel-actions {
  display: flex;
  align-items: center;
}

/* 树形目录 */
.chapter-tree {
  flex: 1;
  overflow-y: auto;
  padding: 4px 0;
  font-size: 13px;
}
.tree-loading,
.tree-empty {
  padding: 24px 16px;
  text-align: center;
  color: #909399;
  font-size: 13px;
}
.tree-empty p {
  margin-bottom: 12px;
}
.tree-node {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-right: 4px;
  overflow: hidden;
}
.tree-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tree-ops {
  display: none;
  gap: 6px;
  flex-shrink: 0;
}
.tree-node:hover .tree-ops {
  display: flex;
}
.tree-op-btn {
  color: #909399;
  cursor: pointer;
  font-size: 13px;
  padding: 2px;
}
.tree-op-btn:hover {
  color: #409eff;
}
.tree-op-del:hover {
  color: #f56c6c;
}

/* 编辑区 */
.chapter-title-wrap {
  flex: 1;
  margin-right: 16px;
}
.chapter-meta {
  font-size: 12px;
  color: #909399;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  flex-shrink: 0;
}
.save-status { font-size: 11px; }
.save-saving { color: #e6a23c; }
.save-saved  { color: #67c23a; }
.save-error  { color: #f56c6c; }

.editor-wrapper {
  flex: 1;
  overflow: hidden;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
}
.editor-placeholder {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #c0c4cc;
  font-size: 14px;
  gap: 8px;
}
.editor-placeholder i {
  font-size: 36px;
}
.editor {
  flex: 1;
}
.editor ::v-deep .el-textarea__inner {
  height: 100%;
  resize: none;
  background: #ffffff;
  color: #303133;
  border-color: #dcdfe6;
  font-size: 15px;
  line-height: 1.8;
}

/* AI 及人物 */
.ai-tools {
  padding: 10px 12px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  border-bottom: 1px solid #ebeef5;
}
.ai-result {
  padding: 12px;
  font-size: 13px;
}
.ai-result-title {
  margin-bottom: 6px;
  color: #909399;
  font-size: 12px;
}
.ai-result-body {
  line-height: 1.8;
  color: #303133;
}
.role-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-bottom: 1px solid #ebeef5;
}
.role-panel-count {
  font-size: 12px;
  color: #909399;
}
.role-loading {
  padding: 24px;
  text-align: center;
  color: #909399;
  font-size: 13px;
}
.setting-list {
  list-style: none;
  padding: 0 0 8px;
  margin: 0;
  font-size: 13px;
  overflow-y: auto;
}
.setting-item {
  padding: 10px 12px;
  border-bottom: 1px solid #f5f7fa;
  transition: background 0.15s;
}
.setting-item:hover {
  background: #fafafa;
}
.setting-item:hover .setting-ops {
  opacity: 1;
}
.setting-empty {
  color: #c0c4cc;
  border: none !important;
  text-align: center;
  padding: 24px 12px;
  font-size: 12px;
}
.setting-empty p { margin: 0; }
.setting-title {
  font-weight: 600;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
}
.setting-ops {
  margin-left: auto;
  display: flex;
  gap: 8px;
  opacity: 0;
  transition: opacity 0.15s;
}
.setting-op-btn,
.setting-op-del {
  cursor: pointer;
  font-size: 13px;
  color: #909399;
}
.setting-op-btn:hover { color: #409eff; }
.setting-op-del:hover { color: #f56c6c; }
.setting-badge {
  font-size: 11px;
  font-weight: normal;
  padding: 1px 5px;
  border-radius: 3px;
}
.badge-main {
  background: #fdf6ec;
  color: #e6a23c;
}
.badge-support {
  background: #ecf5ff;
  color: #409eff;
}
.setting-meta {
  font-size: 12px;
  color: #909399;
  margin-bottom: 3px;
  display: flex;
  gap: 8px;
}
.setting-desc {
  color: #606266;
  font-size: 12px;
  line-height: 1.5;
  margin-bottom: 2px;
}
.setting-bio {
  color: #909399;
  font-size: 12px;
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
